import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { cognitoSignIn, cognitoUpdateEmail } from '../login/cognito';
import { Button } from '../../components/Button';

const COGNITO_CLIENT_ID = process.env.REACT_APP_CLIENT_ID ?? '';
const COGNITO_REGION = 'eu-central-1';

export interface EmailChangeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onEmailChangeInitiated: (accessToken: string, newEmail: string) => void;
  currentEmail: string;
}

export default function EmailChangeModal(props: EmailChangeModalProps) {
  const [step, setStep] = useState<'auth' | 'new-email'>('auth');
  const [currentEmail, setCurrentEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleReAuth = async () => {
    if (!currentEmail.trim()) {
      setError('Please enter your current email address');
      return;
    }
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setIsProcessing(true);
    setError('');

    const result = await cognitoSignIn(COGNITO_REGION, COGNITO_CLIENT_ID, currentEmail, password);

    if (result.success) {
      setStep('new-email');
    } else {
      setError('Invalid email or password. Please try again.');
    }
    setIsProcessing(false);
  };

  const handleEmailChange = async () => {
    if (!newEmail.trim()) {
      setError('Please enter a new email address');
      return;
    }

    if (newEmail === currentEmail) {
      setError('New email must be different from current email');
      return;
    }

    setIsProcessing(true);
    setError('');

    // Get access token from the auth result
    const authResult = await cognitoSignIn(
      COGNITO_REGION,
      COGNITO_CLIENT_ID,
      currentEmail,
      password,
    );

    if (authResult.success && authResult.data.AccessToken) {
      const updateResult = await cognitoUpdateEmail(
        COGNITO_REGION,
        authResult.data.AccessToken,
        newEmail,
      );

      if (updateResult.success) {
        props.onEmailChangeInitiated(authResult.data.AccessToken, newEmail);
        props.setOpen(false);
      } else {
        if (updateResult.error === 'EmailAlreadyExists') {
          setError('This email is already in use. Please choose a different email.');
        } else {
          setError('Failed to update email. Please try again.');
        }
      }
    } else {
      setError('Authentication failed. Please try again.');
    }

    setIsProcessing(false);
  };

  const handleClose = () => {
    setStep('auth');
    setCurrentEmail('');
    setPassword('');
    setNewEmail('');
    setError('');
    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={handleClose} className="relative z-10">
      {/* DialogBackdrop */}
      <div className="fixed inset-0 bg-neutral-dark/75 transition-opacity" aria-hidden="true"></div>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            className="relative transform overflow-hidden rounded-lg bg-surface px-4 pt-5 pb-4 text-left
            shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
          >
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title as="h3" className="text-base font-semibold text-main">
                  {step === 'auth' ? 'Verify Your Identity' : 'Change Email Address'}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-muted">
                    {step === 'auth'
                      ? 'Please enter your password to verify your identity before changing your email.'
                      : 'Enter your new email address. You will receive a verification code to confirm the change.'}
                  </p>
                </div>
              </div>
            </div>

            {step === 'auth' && (
              <div className="mt-4">
                <div>
                  <label htmlFor="current-email" className="block text-sm font-medium text-main">
                    Current Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="current-email"
                      name="current-email"
                      type="email"
                      required
                      value={currentEmail}
                      onChange={(e) => setCurrentEmail(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-surface px-3 py-1.5 text-base text-main
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-disabled focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter your current email"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="password" className="block text-sm font-medium text-main">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-surface px-3 py-1.5 text-base text-main
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-disabled focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-error">{error}</p>}
                <div className="mt-5 sm:mt-6">
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleReAuth}
                    disabled={isProcessing}
                    loading={isProcessing}
                    fullWidth
                  >
                    {isProcessing ? 'Verifying...' : 'Verify Identity'}
                  </Button>
                </div>
              </div>
            )}

            {step === 'new-email' && (
              <div className="mt-4">
                <div>
                  <label htmlFor="current-email" className="block text-sm font-medium text-main">
                    Current Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="current-email"
                      name="current-email"
                      type="email"
                      value={props.currentEmail}
                      disabled
                      className="block w-full rounded-md bg-muted px-3 py-1.5 text-base text-muted
                      outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm/6"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="new-email" className="block text-sm font-medium text-main">
                    New Email
                  </label>
                  <div className="mt-1">
                    <input
                      id="new-email"
                      name="new-email"
                      type="email"
                      required
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-surface px-3 py-1.5 text-base text-main
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-disabled focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter new email address"
                    />
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-error">{error}</p>}
                <div className="mt-5 sm:mt-6 flex gap-3">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setStep('auth')}
                    disabled={isProcessing}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleEmailChange}
                    disabled={isProcessing}
                    loading={isProcessing}
                    className="flex-1"
                  >
                    {isProcessing ? 'Updating...' : 'Update Email'}
                  </Button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
